import React, { useState, useRef } from 'react';
import { mapValues, trim } from 'lodash';
import {
  Flex, Text, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import { questions } from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import { getUpdatedQuestions } from '../helpers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

export default function DelawareForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  version,
  formLink,
  isOnboardingV2,
}) {
  const prefilledQuestions = getInitialQuestions(questions);
  const [formQs, setFormQs] = useState(prefilledQuestions);
  const formRef = useRef();
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const renderForm = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={formQs}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={(props) => setFormQs(getUpdatedQuestions(props))}
        ref={formRef}
      />
    </Flex>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderForm()}
        </Flex>
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (formRef && formRef.current) {
              formRef.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (err) return;

            const data = formQs.reduce(
              (acc, question) => ({ ...acc, ...question.answer.answer }),
              {},
            );
            const trimmedData = mapValues(data, trim);
            saveFormData(trimmedData, () => {
              setCurrentStep(STEPS.step2);
            });
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderForm(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" />
      </Flex>
    </Flex>
  );
  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}
